import {
  Component,
  ChangeDetectionStrategy,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';

import { RegistrationsComponent } from '@dpr-details/models/component';
import {
  DprConsumableDetailGridData,
  DprDetailsGridBaseComponent,
} from '@dpr-details/models/grid';
import { DailyStockService } from '@dpr-details/services';
import { DprConsumableGridService } from '@dpr-details/services/grid';
import { NumericEditComponent, TextDisplayComponent } from '@grid/components';
import { GridAlignment, GridCellContentAlignment } from '@grid/enums';
import { GridColumn, GridOptions } from '@grid/models';
import { DecimalDisplayComponent } from '@shared/components';
import { GridColumns, GridDefaultLabels } from '@shared/utils/grid';
import { Constants, getStaticMessage } from '@shared/utils';
import { StaticMessage } from '@shared/enums';

@UntilDestroy()
@Component({
  selector: 'app-dpr-consumables-grid',
  templateUrl: './dpr-consumables-grid.component.html',
  styleUrls: ['./dpr-consumables-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DprConsumablesGridComponent
  extends DprDetailsGridBaseComponent<DprConsumableDetailGridData>
  implements RegistrationsComponent<DprConsumableDetailGridData>, OnChanges
{
  enableStock = true;

  constructor(
    private dprConsumableGridService: DprConsumableGridService,
    private dailyStockService: DailyStockService
  ) {
    super();
  }

  //#region Getters

  private get consumableGridColumns(): GridColumn<DprConsumableDetailGridData>[] {
    return this.enableStock
      ? this.stockVersionColumns
      : this.basicVersionColumns;
  }

  private get stockVersionColumns(): GridColumn<DprConsumableDetailGridData>[] {
    return [
      {
        columnDef: 'dailyStockDetailsTotalStartOfDay',
        header: {
          name: $localize`:@@dprDetails.dprConsumablesGrid.colTotalStartOfDay:Total aboard start of day`,
          alignment: GridAlignment.Right,
        },
        columnAlignment: GridAlignment.Right,
        minWidth: 80,
        display: {
          type: DecimalDisplayComponent,
          config: {
            alignment: GridCellContentAlignment.Right,
          },
        },
        tabIndex: -1,
        disableFocusedBackground: true,
        allowSorting: false,
        allowEditing: false,
      },
      {
        columnDef: 'added',
        header: {
          name: $localize`:@@dprDetails.dprConsumablesGrid.colAdded:Added today`,
          alignment: GridAlignment.Right,
        },
        columnAlignment: GridAlignment.Right,
        minWidth: 80,
        display: {
          type: DecimalDisplayComponent,
        },
        edit: {
          type: NumericEditComponent,
          config: {
            options: {
              allowDecimals: true,
              step: 0.01,
              decimalPrecision: 2,
              max: Constants.NumericOptions.maxConsumables,
              min: Constants.NumericOptions.minConsumables,
            },
            alignment: GridCellContentAlignment.Right,
          },
        },
        onCellEdited: this.addedChanged.bind(this),
        allowSorting: false,
        allowEditing: this.canEdit,
      },
      {
        columnDef: 'used',
        header: {
          name: $localize`:@@dprDetails.dprConsumablesGrid.colUsed:Used today`,
          alignment: GridAlignment.Right,
        },
        columnAlignment: GridAlignment.Right,
        minWidth: 80,
        display: {
          type: DecimalDisplayComponent,
        },
        edit: {
          type: NumericEditComponent,
          config: {
            options: {
              allowDecimals: true,
              min: 0,
              step: 0.01,
              decimalPrecision: 2,
              max: Constants.NumericOptions.maxConsumables,
            },
            alignment: GridCellContentAlignment.Right,
          },
        },
        onCellEdited: this.usedChanged.bind(this),
        allowSorting: false,
        allowEditing: this.canEdit,
      },
      {
        columnDef: 'dailyStockDetailsTotalEndOfDay',
        header: {
          name: $localize`:@@dprDetails.dprConsumablesGrid.colTotalEndOfDay:Total aboard end of day`,
          alignment: GridAlignment.Right,
        },
        columnAlignment: GridAlignment.Right,
        minWidth: 80,
        display: {
          type: DecimalDisplayComponent,
          config: {
            alignment: GridCellContentAlignment.Right,
          },
        },
        tabIndex: -1,
        disableFocusedBackground: true,
        allowSorting: false,
        allowEditing: false,
      },
      {
        columnDef: 'dailyStockDetailsTotalUsed',
        header: {
          name: $localize`:@@dprDetails.dprConsumablesGrid.colTotalUsed:Total used`,
          alignment: GridAlignment.Right,
        },
        columnAlignment: GridAlignment.Right,
        minWidth: 80,
        display: {
          type: DecimalDisplayComponent,
          config: {
            alignment: GridCellContentAlignment.Right,
          },
        },
        allowSorting: false,
        allowEditing: false,
      },
    ];
  }

  private get basicVersionColumns(): GridColumn<DprConsumableDetailGridData>[] {
    return [
      {
        columnDef: 'previous',
        header: {
          name: getStaticMessage(StaticMessage.PreviousColumn),
          alignment: GridAlignment.Right,
        },
        columnAlignment: GridAlignment.Right,
        minWidth: 80,
        display: {
          type: DecimalDisplayComponent,
          config: {
            alignment: GridCellContentAlignment.Right,
          },
        },
        tabIndex: -1,
        disableFocusedBackground: true,
        allowSorting: false,
        allowEditing: false,
      },
      {
        columnDef: 'today',
        header: {
          name: getStaticMessage(StaticMessage.TodayColumn),
          alignment: GridAlignment.Right,
        },
        columnAlignment: GridAlignment.Right,
        minWidth: 80,
        display: {
          type: DecimalDisplayComponent,
        },
        edit: {
          type: NumericEditComponent,
          config: {
            options: {
              allowDecimals: true,
              step: 0.01,
              decimalPrecision: 2,
              min: Constants.NumericOptions.minConsumables,
              max: Constants.NumericOptions.maxConsumables,
            },
            alignment: GridCellContentAlignment.Right,
          },
        },
        onCellEdited: this.todayChanged.bind(this),
        allowSorting: false,
        allowEditing: this.canEdit,
      },
      {
        columnDef: 'total',
        header: {
          name: getStaticMessage(StaticMessage.TotalColumn),
          alignment: GridAlignment.Right,
        },
        columnAlignment: GridAlignment.Right,
        minWidth: 80,
        display: {
          type: DecimalDisplayComponent,
          config: {
            alignment: GridCellContentAlignment.Right,
          },
        },
        tabIndex: -1,
        disableFocusedBackground: true,
        allowSorting: false,
        allowEditing: false,
      },
    ];
  }

  //#endregion

  //#region Lifecycle Hooks

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.predefinedChanges?.currentValue) {
      this.gridComponent?.api?.setPredefinedChanges?.(this.predefinedChanges);
    }

    if (changes.selectedSubproject?.currentValue && this.labeling) {
      if (this.selectedSubproject) {
        this.enableStock =
          this.selectedSubproject.data.subprojectConfiguration.enableStockForConsumables;
      }

      this.gridColumns$.next(this.getGridColumns());
    }

    if (changes.canEdit) {
      this.gridOptions$.next(this.getGridOptions());
      this.gridColumns$.next(this.getGridColumns());
    }
  }

  //#endregion

  protected getGridOptions(): GridOptions<DprConsumableDetailGridData> {
    return {
      sequenceConfig: {
        disabled: true,
      },
      labeling: GridDefaultLabels,
      addConfig: {
        allowAdding: false,
        keepAutogeneratedIds: true,
      },
      editConfig: {
        allowEditing: this.canEdit,
      },
      deleteConfig: {
        allowDeleting: false,
      },
      saveConfig: {
        isExternalSave: true,
      },
      sortConfig: {
        allowSorting: false,
      },
      rowConfig: {
        allowSelection: false,
        allowDeleting: false,
      },
      selectionConfig: {
        allowSelection: false,
      },
      events: {
        onGridRegistered: this.onGridRegistered.bind(this),
      },
    };
  }

  protected getGridColumns(): GridColumn<DprConsumableDetailGridData>[] {
    return [
      {
        columnDef: 'name',
        header: {
          name: $localize`:@@dprDetails.dprConsumablesGrid.colCategory:Category`,
        },
        minWidth: 200,
        display: {
          type: TextDisplayComponent,
        },
        tabIndex: -1,
        disableFocusedBackground: true,
        allowSorting: false,
        allowEditing: false,
      },
      ...this.consumableGridColumns,
      {
        ...GridColumns.comment,
        minWidth: this.enableStock ? 70 : 200,
      },
    ];
  }

  //#region Private methods

  private todayChanged(data: DprConsumableDetailGridData): void {
    data.total = this.dprConsumableGridService.calculateTotal(
      data.previous,
      data.today
    );
  }

  private addedChanged(data: DprConsumableDetailGridData): void {
    data.dailyStockDetailsTotalEndOfDay = this.dailyStockService.onAddedChanged(
      data,
      data.dailyStockDetails
    )?.totalEndOfDay;
  }

  private usedChanged(
    data: DprConsumableDetailGridData,
    oldValue: number
  ): void {
    const updatedDailyStockDetails = this.dailyStockService.onUsedChanged(
      data,
      data.dailyStockDetails,
      oldValue
    );

    data.dailyStockDetailsTotalEndOfDay =
      updatedDailyStockDetails.totalEndOfDay;
    data.dailyStockDetailsTotalUsed = updatedDailyStockDetails.totalUsed;
  }

  //#endregion
}
